import { useEffect, useState } from 'react';
import { Pencil, Plus, Radio, Trash2 } from 'lucide-react';
import type { ButtonMode, IntercomConfig, Partyline } from '@livepuls/types';
import { useConfigStore } from '../store/config-store.js';
import { useUiStore } from '../store/ui-store.js';
import { api } from '../lib/api.js';
import { Button } from '../components/ui/Button.js';
import { Field } from '../components/ui/Field.js';
import { Modal } from '../components/ui/Modal.js';
import { EmptyState } from '../components/ui/EmptyState.js';

export function PartylinesPage(): JSX.Element {
  const draft = useConfigStore((s) => s.draft);
  const loadDraft = useConfigStore((s) => s.loadDraft);
  const select = useUiStore((s) => s.select);
  const selection = useUiStore((s) => s.selection);

  // Modal-Zustand: entweder „neu anlegen" oder „bestehende Partyline bearbeiten".
  const [modal, setModal] = useState<
    { mode: 'create' } | { mode: 'edit'; partyline: Partyline } | null
  >(null);

  if (!draft) {
    return <div className="p-6 text-text-dim">Lade Konfiguration…</div>;
  }

  return (
    <div className="p-6 space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-text">Partylines</h1>
          <p className="text-sm text-text-dim mt-1">
            {draft.partylines.length} Partyline
            {draft.partylines.length === 1 ? '' : 's'} im Draft.
          </p>
        </div>
        <Button
          variant="primary"
          size="sm"
          leftIcon={<Plus size={14} />}
          onClick={() => setModal({ mode: 'create' })}
        >
          Neue Partyline
        </Button>
      </header>

      {draft.partylines.length === 0 ? (
        <EmptyState
          icon={<Radio size={32} />}
          title="Noch keine Partylines"
          description="Partylines sind n:n-Konferenzen, denen Panels per Membership beitreten."
          action={
            <Button
              variant="primary"
              size="sm"
              leftIcon={<Plus size={14} />}
              onClick={() => setModal({ mode: 'create' })}
            >
              Erste Partyline anlegen
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {draft.partylines.map((pl) => {
            const isSelected = selection.kind === 'partyline' && selection.id === pl.id;
            const memberNames = pl.members
              .map((m) => draft.panels.find((p) => p.id === m.panelId)?.name)
              .filter((n): n is string => !!n);
            return (
              <section
                key={pl.id}
                onClick={() => {
                  select('partyline', pl.id);
                  setModal({ mode: 'edit', partyline: pl });
                }}
                title="Zum Bearbeiten klicken (Mitglieder hinzufügen/entfernen)"
                className={[
                  'card p-4 cursor-pointer',
                  isSelected ? 'ring-1 ring-accent' : '',
                ].join(' ')}
              >
                <header className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-text">{pl.name}</h3>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        select('partyline', pl.id);
                        setModal({ mode: 'edit', partyline: pl });
                      }}
                      className="text-text-mute hover:text-accent transition"
                      aria-label="Bearbeiten"
                      title="Mitglieder bearbeiten"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!confirm(`Partyline "${pl.name}" löschen?`)) return;
                        await api.delete(`/api/v1/partylines/${pl.id}`);
                        await loadDraft();
                      }}
                      className="text-text-mute hover:text-err transition"
                      aria-label="Löschen"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </header>
                <p className="text-xs text-text-dim">
                  {pl.members.length} Mitglied{pl.members.length === 1 ? '' : 'er'}
                  {(pl.deviceIds?.length ?? 0) > 0
                    ? ` · ${pl.deviceIds?.length} Gerät${pl.deviceIds?.length === 1 ? '' : 'e'}`
                    : ''}
                </p>
                {memberNames.length > 0 ? (
                  <p className="text-xxs text-text-mute mt-1 line-clamp-2">{memberNames.join(', ')}</p>
                ) : null}

                {/* Externe Geräte der Partyline bearbeiten. */}
                {(draft.externalDevices?.length ?? 0) > 0 ? (
                  <details className="mt-2" onClick={(e) => e.stopPropagation()}>
                    <summary className="text-xxs uppercase tracking-wider text-text-mute cursor-pointer hover:text-accent">
                      Externe Geräte ({pl.deviceIds?.length ?? 0})
                    </summary>
                    <div className="mt-1.5 space-y-1">
                      {(draft.externalDevices ?? []).map((d) => {
                        const on = (pl.deviceIds ?? []).includes(d.id);
                        return (
                          <label
                            key={d.id}
                            className="flex items-center gap-2 px-2 py-1 rounded border border-border bg-surface cursor-pointer text-sm"
                          >
                            <input
                              type="checkbox"
                              checked={on}
                              onChange={async (e) => {
                                const cur = new Set(pl.deviceIds ?? []);
                                if (e.target.checked) cur.add(d.id);
                                else cur.delete(d.id);
                                await api.put(`/api/v1/partylines/${pl.id}`, {
                                  deviceIds: Array.from(cur),
                                });
                                await loadDraft();
                              }}
                            />
                            <span className="flex-1 truncate text-text">{d.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  </details>
                ) : null}
              </section>
            );
          })}
        </div>
      )}

      <PartylineModal
        open={modal !== null}
        editing={modal?.mode === 'edit' ? modal.partyline : null}
        draft={draft}
        onClose={() => setModal(null)}
        onSaved={async () => {
          setModal(null);
          await loadDraft();
        }}
      />
    </div>
  );
}

function PartylineModal({
  open,
  editing,
  draft,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Gesetzt = bestehende Partyline bearbeiten; null = neu anlegen. */
  editing: Partyline | null;
  draft: IntercomConfig;
  onClose: () => void;
  onSaved: () => Promise<void>;
}): JSX.Element {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Nur Standard-Panels (mit Tasten) können einer Partyline beitreten.
  const eligible = draft.panels.filter((p) => p.kind !== 'source');
  const devices = draft.externalDevices ?? [];
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  // Pro Panel: Tasten-Modus (PTT = Push-to-Talk, Latching = Einrasten).
  const [modes, setModes] = useState<Record<string, ButtonMode>>({});
  // Externe Geräte (bidi) als Mitglieder.
  const [selDevices, setSelDevices] = useState<Record<string, boolean>>({});

  // Beim Öffnen vorbefüllen: im Edit-Modus aus der Partyline, sonst „alle an".
  useEffect(() => {
    if (!open) return;
    if (editing) {
      const memberIds = new Set(editing.members.map((m) => m.panelId));
      setSelected(Object.fromEntries(eligible.map((p) => [p.id, memberIds.has(p.id)])));
      setModes(Object.fromEntries(editing.members.map((m) => [m.panelId, m.mode])));
      setSelDevices(Object.fromEntries((editing.deviceIds ?? []).map((id) => [id, true])));
      setName(editing.name);
    } else {
      setSelected(Object.fromEntries(eligible.map((p) => [p.id, true])));
      setModes(Object.fromEntries(eligible.map((p) => [p.id, 'ptt'] as const)));
      setSelDevices({});
      setName('');
    }
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing?.id]);

  const selectedIds = eligible.filter((p) => selected[p.id]).map((p) => p.id);
  const selDeviceIds = devices.filter((d) => selDevices[d.id]).map((d) => d.id);
  const modeOf = (panelId: string): ButtonMode => modes[panelId] ?? 'ptt';
  // Bestehende Mitglieds-Lautstärke im Edit-Modus erhalten (Default 100).
  const volumeOf = (panelId: string): number =>
    editing?.members.find((m) => m.panelId === panelId)?.volume ?? 100;

  /** Partyline-Button eines Panels für DIESE Partyline finden (falls vorhanden). */
  function findMemberButton(panelId: string, partylineId: string): { id: string } | null {
    const btn = draft.buttons.find(
      (b) =>
        b.type === 'partyline' &&
        b.sourcePanelId === panelId &&
        b.partylineId === partylineId,
    );
    return btn ? { id: btn.id } : null;
  }

  /** Einem Panel einen Partyline-Button im ersten freien Slot anlegen.
   *  Idempotent: hat das Panel bereits einen Partyline-Button für diese
   *  Partyline (z. B. manuell in der Panel-Ansicht angelegt), wird KEIN
   *  zweiter erzeugt – nur der Modus angeglichen. */
  async function addMemberButton(panelId: string, partylineId: string): Promise<void> {
    const existing = findMemberButton(panelId, partylineId);
    if (existing) {
      await api.put(`/api/v1/buttons/${existing.id}`, { mode: modeOf(panelId) });
      return;
    }
    const panel = draft.panels.find((p) => p.id === panelId);
    if (!panel) return;
    const free = panel.layout.find((s) => s.buttonId === null);
    if (!free) return; // Panel voll → überspringen
    const btn = await api.post<{ id: string }>('/api/v1/buttons', {
      type: 'partyline',
      sourcePanelId: panelId,
      partylineId,
      mode: modeOf(panelId),
      volume: 100,
    });
    try {
      const layout = panel.layout.map((s) =>
        s.position === free.position ? { ...s, buttonId: btn.id } : s,
      );
      await api.put(`/api/v1/panels/${panelId}`, { layout });
    } catch (err) {
      // Slot-Zuweisung fehlgeschlagen → verwaisten Button wieder entfernen.
      await api.delete(`/api/v1/buttons/${btn.id}`).catch(() => {});
      throw err;
    }
  }

  async function save(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      if (editing) {
        const plId = editing.id;
        const oldIds = new Set(editing.members.map((m) => m.panelId));
        const nextIds = new Set(selectedIds);
        const added = selectedIds.filter((id) => !oldIds.has(id));
        const removed = [...oldIds].filter((id) => !nextIds.has(id));

        // 1) Entfernte Mitglieder: deren Partyline-Button löschen. Der Panel-Slot
        //    wird per FK „ON DELETE SET NULL" automatisch wieder frei.
        for (const panelId of removed) {
          const btn = findMemberButton(panelId, plId);
          if (btn) await api.delete(`/api/v1/buttons/${btn.id}`);
        }
        // 2) Neue Mitglieder: Partyline-Button anlegen.
        for (const panelId of added) {
          await addMemberButton(panelId, plId);
        }
        // 3) Bleibende Mitglieder mit geändertem Modus: Button-Modus nachziehen.
        for (const panelId of selectedIds) {
          if (added.includes(panelId)) continue;
          const prev = editing.members.find((m) => m.panelId === panelId);
          if (prev && prev.mode !== modeOf(panelId)) {
            const btn = findMemberButton(panelId, plId);
            if (btn) await api.put(`/api/v1/buttons/${btn.id}`, { mode: modeOf(panelId) });
          }
        }
        // 4) Name, Mitglieder & Geräte aktualisieren (Membership wird ersetzt).
        await api.put(`/api/v1/partylines/${plId}`, {
          name: name.trim(),
          members: selectedIds.map((panelId) => ({
            panelId,
            mode: modeOf(panelId),
            volume: volumeOf(panelId),
          })),
          deviceIds: selDeviceIds,
        });
      } else {
        // 1) Partyline anlegen.
        const pl = await api.post<Partyline>('/api/v1/partylines', { name: name.trim() });
        // 2) Mitglieder + Geräte setzen.
        if (selectedIds.length > 0 || selDeviceIds.length > 0) {
          await api.put(`/api/v1/partylines/${pl.id}`, {
            members: selectedIds.map((panelId) => ({
              panelId,
              mode: modeOf(panelId),
              volume: 100,
            })),
            deviceIds: selDeviceIds,
          });
          // 3) Pro Mitglied einen Partyline-Button im ersten freien Slot.
          for (const panelId of selectedIds) {
            await addMemberButton(panelId, pl.id);
          }
        }
      }
      await onSaved();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const allSelected = eligible.length > 0 && selectedIds.length === eligible.length;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? `Partyline bearbeiten · ${editing.name}` : 'Neue Partyline'}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={!name.trim() || busy}
            onClick={() => void save()}
          >
            {editing
              ? busy
                ? 'Speichere…'
                : `Speichern (${selectedIds.length})`
              : busy
              ? 'Lege an…'
              : `Anlegen (${selectedIds.length})`}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Partyline-Name" hint="z. B. 'Crew', 'Tech', 'Floor'">
          <input
            autoFocus
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-xxs uppercase tracking-wider text-text-mute">
              Teilnehmer ({selectedIds.length}/{eligible.length})
            </div>
            <button
              type="button"
              className="text-xxs uppercase tracking-wider text-text-mute hover:text-accent"
              onClick={() =>
                setSelected(
                  allSelected ? {} : Object.fromEntries(eligible.map((p) => [p.id, true])),
                )
              }
            >
              {allSelected ? 'Keine' : 'Alle'}
            </button>
          </div>
          <p className="text-xxs text-text-mute mb-2">
            Für jedes ausgewählte Panel wird automatisch ein Partyline-Button im ersten freien
            Slot angelegt. Mit dem Schalter hinter dem Namen legst du fest, ob die Taste{' '}
            <strong>PTT</strong> (Push-to-Talk, hält nur solange gedrückt) oder{' '}
            <strong>Latching</strong> (rastet ein) ist.
          </p>
          <div className="max-h-56 overflow-y-auto space-y-1 pr-1">
            {eligible.length === 0 ? (
              <p className="text-xs text-text-dim">Keine Panels vorhanden.</p>
            ) : (
              eligible.map((p) => {
                const free = p.layout.some((s) => s.buttonId === null);
                const isOn = !!selected[p.id];
                const mode = modeOf(p.id);
                return (
                  <div
                    key={p.id}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-md border border-border bg-surface"
                  >
                    <label className="flex flex-1 items-center gap-2 cursor-pointer min-w-0">
                      <input
                        type="checkbox"
                        checked={isOn}
                        onChange={(e) => setSelected((m) => ({ ...m, [p.id]: e.target.checked }))}
                      />
                      <span className="flex-1 text-sm text-text truncate">{p.name}</span>
                    </label>
                    {!free ? (
                      <span className="text-xxs text-amber-400" title="Panel voll – kein Button">
                        voll
                      </span>
                    ) : null}
                    <ModeToggle
                      mode={mode}
                      disabled={!isOn}
                      onChange={(next) => setModes((m) => ({ ...m, [p.id]: next }))}
                    />
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Externe Geräte (bidi) als Mitglieder. */}
        {devices.length > 0 ? (
          <div>
            <div className="text-xxs uppercase tracking-wider text-text-mute mb-1.5">
              Externe Geräte ({selDeviceIds.length}/{devices.length})
            </div>
            <p className="text-xxs text-text-mute mb-2">
              Das Gerät hört die Partyline auf seinem Lautsprecher und sein Mikrofon wird von allen
              Partyline-Panels mitgehört (talk + listen).
            </p>
            <div className="space-y-1">
              {devices.map((d) => (
                <label
                  key={d.id}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-md border border-border bg-surface cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={!!selDevices[d.id]}
                    onChange={(e) => setSelDevices((m) => ({ ...m, [d.id]: e.target.checked }))}
                  />
                  <span className="flex-1 text-sm text-text truncate">{d.name}</span>
                  {!d.micPortKey || !d.spkPortKey ? (
                    <span className="text-xxs text-amber-400" title="Gerät unvollständig gepaart">
                      mic/spk fehlt
                    </span>
                  ) : null}
                </label>
              ))}
            </div>
          </div>
        ) : null}

        {error ? <div className="text-xs text-err break-words">Fehler: {error}</div> : null}
      </div>
    </Modal>
  );
}

/**
 * Schiebe-Toggle zwischen PTT (links) und Latching (rechts) für ein Panel.
 */
function ModeToggle({
  mode,
  disabled,
  onChange,
}: {
  mode: ButtonMode;
  disabled?: boolean;
  onChange: (mode: ButtonMode) => void;
}): JSX.Element {
  const isLatching = mode === 'latching';
  return (
    <button
      type="button"
      role="switch"
      aria-checked={isLatching}
      disabled={disabled}
      onClick={() => onChange(isLatching ? 'ptt' : 'latching')}
      title={isLatching ? 'Latching (rastet ein)' : 'PTT (Push-to-Talk)'}
      className={[
        'flex items-center gap-1.5 select-none',
        disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer',
      ].join(' ')}
    >
      <span
        className={[
          'text-xxs uppercase tracking-wider',
          !isLatching ? 'text-accent' : 'text-text-mute',
        ].join(' ')}
      >
        PTT
      </span>
      <span
        className={[
          'relative h-4 w-7 rounded-full transition-colors',
          isLatching ? 'bg-accent' : 'bg-border',
        ].join(' ')}
      >
        <span
          className={[
            'absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all',
            isLatching ? 'left-3.5' : 'left-0.5',
          ].join(' ')}
        />
      </span>
      <span
        className={[
          'text-xxs uppercase tracking-wider',
          isLatching ? 'text-accent' : 'text-text-mute',
        ].join(' ')}
      >
        Latch
      </span>
    </button>
  );
}
